import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { useAuth } from "@clerk/clerk-react";
import { getServerURL } from "../../../utils/funcs/get-serverURL";
import LoadingSpinner from "../../loading-spinner";

interface DocEntry {
  filename: string;
  title: string;
}

export default function AdminDocs() {
  const { getToken } = useAuth();
  const serverURL = getServerURL();

  const [docs, setDocs] = useState<DocEntry[]>([]);
  const [selectedDoc, setSelectedDoc] = useState<DocEntry | null>(null);
  const [content, setContent] = useState<string>("");
  const [loadingList, setLoadingList] = useState(true);
  const [loadingDoc, setLoadingDoc] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const getAuthHeader = useCallback(async () => {
    const token = await getToken();
    return token ? { Authorization: `Bearer ${token}` } : {};
  }, [getToken]);

  useEffect(() => {
    const fetchDocs = async () => {
      try {
        setLoadingList(true);
        setError(null);
        const headers = await getAuthHeader();
        const response = await axios.get(`${serverURL}/admin/docs`, { headers });
        if (response.data.success) {
          setDocs(response.data.docs);
        } else {
          setError("Failed to load docs");
        }
      } catch {
        setError("Error loading docs from server");
      } finally {
        setLoadingList(false);
      }
    };
    fetchDocs();
  }, [getAuthHeader, serverURL]);

  const openDoc = useCallback(async (doc: DocEntry) => {
    try {
      setSelectedDoc(doc);
      setContent("");
      setLoadingDoc(true);
      setError(null);
      const headers = await getAuthHeader();
      const response = await axios.get(`${serverURL}/admin/docs/${encodeURIComponent(doc.filename)}`, { headers });
      if (response.data.success) {
        setContent(response.data.content);
      } else {
        setError("Failed to load document");
      }
    } catch {
      setError("Error loading document");
    } finally {
      setLoadingDoc(false);
    }
  }, [getAuthHeader, serverURL]);

  return (
    <>
      <div style={{ display: "flex", gap: "0", minHeight: "60vh" }}>
      {/* Sidebar */}
      <div style={{
        width: "200px",
        flexShrink: 0,
        borderRight: "1px solid #e0e0e0",
        paddingRight: "10px",
        marginRight: "24px",
      }}>
        <h3 style={{ fontSize: "1em", fontWeight: "600", color: "#333", marginBottom: "12px", marginTop: 0 , textAlign: "left" }}>
          Documents
        </h3>
        {loadingList ? (
          <div style={{ display: "flex", justifyContent: "center", padding: "18px 0", transform: "scale(0.7)", transformOrigin: "center top" }}>
            <LoadingSpinner />
          </div>
        ) : error && docs.length === 0 ? (
          <p style={{ color: "#c0392b", fontSize: "0.9em" }}>{error}</p>
        ) : (
          <nav style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
            {docs.map((doc) => (
              <button
                key={doc.filename}
                onClick={() => openDoc(doc)}
                style={{
                  textAlign: "left",
                  background: selectedDoc?.filename === doc.filename ? "#e8f8f9" : "transparent",
                  border: "none",
                  borderLeft: selectedDoc?.filename === doc.filename ? "3px solid #00adb5" : "3px solid transparent",
                  padding: "8px 10px",
                  cursor: "pointer",
                  fontSize: "0.88em",
                  color: selectedDoc?.filename === doc.filename ? "#00adb5" : "#444",
                  fontWeight: selectedDoc?.filename === doc.filename ? "600" : "400",
                  borderRadius: "0 4px 4px 0",
                  transition: "all 0.15s",
                  wordBreak: "break-word",
                }}
              >
                {doc.title}
              </button>
            ))}
          </nav>
        )}
      </div>

      {/* Content */}
      <div style={{ flex: 1, minWidth: 0 }}>
        {!selectedDoc ? (
          <div style={{ color: "#888", marginTop: "8px" }}>
            <p>Select a document from the list to read it.</p>
          </div>
        ) : loadingDoc ? (
          <div style={{ display: "flex", justifyContent: "center", padding: "28px 0", transform: "scale(0.8)", transformOrigin: "center" }}>
            <LoadingSpinner />
          </div>
        ) : error ? (
          <p style={{ color: "#c0392b" }}>{error}</p>
        ) : (
          <div style={{
            lineHeight: "1.7",
            color: "#333",
            fontSize: "0.95em",
            maxWidth: "820px",
          }}
            className="admin-docs-content"
          >
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{content}</ReactMarkdown>
          </div>
        )}
      </div>
    </div>
    <div style={{ height: "60px" }} />
    </>
  );
}
